import * as React from 'react';
import {useSelector} from 'react-redux';

import {Session} from 'nutshell-core/session';

import {colors} from 'shells/colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {TeamIcon} from 'shells/icon/svg/team-icon';
import {UpgradePillButton} from 'shells/limits/upgrade-pill-button';
import {TwoLine} from 'shells/two-line/two-line';

import type {MeetingType} from 'nutshell-graphql-types';

import {GraphQLSelectTeams} from '../../../core/select/graphql-select-teams';
import {SchedulerRoundRobinFeatureGate} from '../../../limits/feature-gates/scheduler-round-robin-feature-gate';

import {appHistory} from '../../../../history';
import type {NutshellState} from '../../../../store';

import {ROUND_ROBIN} from './create-meeting-modal';
import {HostTypeSelectRadio} from './host-type-select-radio';

import styles from './meeting-host-type-selection.module.css';

type Props = {
    selectedTeamId: string | null | undefined;
    setSelectedTeamId: (teamId?: string | null | undefined) => void;
    setSelectedCohostIds: (userIds: string[]) => void;
    selectedHostType: MeetingType;
    setSelectedHostType: (hostType: MeetingType) => void;
    canAccessRoundRobin: boolean;
};

export function MeetingRoundRobinSelect(props: Props) {
    const selectContainerRef = React.useRef(null);
    const [isFeatureGateOpen, setIsFeatureGateOpen] = React.useState(false);
    const userPermissions = useSelector((state: NutshellState) =>
        Session.getUserPermissions(state)
    );

    return (
        <div ref={selectContainerRef}>
            <div className='flex align-center justify-sb'>
                <HostTypeSelectRadio
                    value={ROUND_ROBIN}
                    label='Round-robin'
                    sublabel='Book meetings whenever any host is available.'
                    icon={<TeamIcon size={15} wrapWithDiv={false} />}
                    isDisabled={!props.canAccessRoundRobin}
                />
                {!props.canAccessRoundRobin && (
                    <>
                        <UpgradePillButton
                            onClick={() => {
                                setIsFeatureGateOpen(true);
                            }}
                            size='small'
                        />
                        {isFeatureGateOpen && (
                            <SchedulerRoundRobinFeatureGate
                                isOpen={isFeatureGateOpen}
                                onClose={() => {
                                    setIsFeatureGateOpen(false);
                                }}
                            />
                        )}
                    </>
                )}
            </div>
            <div
                onClick={() => {
                    if (props.selectedHostType !== ROUND_ROBIN) {
                        props.setSelectedHostType(ROUND_ROBIN);
                        props.setSelectedCohostIds([]);
                    }
                }}
                ref={selectContainerRef}
                className={
                    props.selectedHostType !== ROUND_ROBIN
                        ? styles['select-container--disabled']
                        : styles['select-container']
                }
            >
                {props.selectedHostType === ROUND_ROBIN && (
                    <GraphQLSelectTeams
                        name='round-robin-select'
                        placeholder='Select team…'
                        defaultsToFirstItem={false}
                        onChange={(selectedOption) => {
                            props.setSelectedTeamId(selectedOption.value);
                        }}
                        onClear={() => {
                            props.setSelectedTeamId(null);
                        }}
                        selectedId={props.selectedTeamId}
                        menuPortalTarget={
                            // Portals the select menu within the container so that it can overhang outside the modal
                            // @ts-expect-error
                            selectContainerRef.current instanceof HTMLElement
                                ? selectContainerRef.current
                                : undefined
                        }
                        isMulti={false}
                        // @ts-expect-error
                        noResultsText='No teams found'
                        formatOptionLabel={(option) => (
                            <TwoLine
                                key={option.value}
                                topRow={option.label}
                                bottomRow={
                                    // Do not show member count if the team is already selected, and check for 1 member

                                    props.selectedTeamId === option.value
                                        ? null
                                        : option.numMembers === 1
                                        ? '1 member'
                                        : `${option.numMembers} members`
                                }
                                avatar={<TeamIcon size={24} color={colors.orangeDk} />}
                                shouldCenterSingleLine={true}
                                isLargeWithSmallBottomRow={true}
                                isDisabled={option.isDisabled}
                            />
                        )}
                        tokenizedStyle='teams'
                        footerButton={
                            userPermissions && userPermissions.canAccessSetup
                                ? {
                                      label: 'Manage teams',
                                      onClick: () => appHistory.push('/setup/user'),
                                  }
                                : undefined
                        }
                    />
                )}
            </div>
        </div>
    );
}
